import React from 'react';
import { Divider, Tab, Tabs } from '@mui/material';
import AdminAssistCoachingPromptTab from './VeraAdminChatAssistPromptSandbox/AdminAssistCoachingPromptTab';
import AdminAssistContactSelector from './VeraAdminChatAssistPromptSandbox/AdminAssistContactSelector';
import AdminAssistDraftBanner from './VeraAdminChatAssistPromptSandbox/AdminAssistDraftBanner';
import AdminAssistGroundingPromptTab from './VeraAdminChatAssistPromptSandbox/AdminAssistGroundingPromptTab';
import AdminAssistIntentPromptTab from './VeraAdminChatAssistPromptSandbox/AdminAssistIntentPromptTab';
import AdminAssistPreviewSection from './VeraAdminChatAssistPromptSandbox/AdminAssistPreviewSection';
import AdminAssistProposalFooter from './VeraAdminChatAssistPromptSandbox/AdminAssistProposalFooter';
import AdminAssistTranscriptSection from './VeraAdminChatAssistPromptSandbox/AdminAssistTranscriptSection';
import { useVeraAdminChatAssistPromptSandbox } from './VeraAdminChatAssistPromptSandbox/useVeraAdminChatAssistPromptSandbox';

export type { PreviewAssistTranscriptMessage } from './VeraAdminChatAssistPromptSandbox/veraAdminChatAssistPromptSandboxUtils';

export default function VeraAdminChatAssistPromptSandbox() {
  const sandbox = useVeraAdminChatAssistPromptSandbox();

  return (
    <>
      {sandbox.savedDraft && (
        <AdminAssistDraftBanner
          savedDraft={sandbox.savedDraft}
          onRestore={sandbox.restoreDraft}
          onDismiss={sandbox.dismissDraft}
        />
      )}

      <Tabs
        value={sandbox.promptTab}
        onChange={(_, value) => sandbox.setPromptTab(value)}
        sx={{ mb: 3, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Tab label="Coaching Prompt" />
        <Tab label="Intent Prompt" />
        <Tab label="Google Search Prompt" />
      </Tabs>

      {sandbox.promptTab === 0 && (
        <AdminAssistCoachingPromptTab
          markdown={sandbox.markdown}
          productionMarkdown={sandbox.productionMarkdown}
          rawMode={sandbox.rawMode}
          onRawModeChange={sandbox.setRawMode}
          onMarkdownChange={sandbox.handleRawMarkdownChange}
          onRawBlur={sandbox.syncEditorFromMarkdown}
          editor={sandbox.editor}
        />
      )}

      {sandbox.promptTab === 1 && (
        <AdminAssistIntentPromptTab
          intentText={sandbox.intentText}
          intentProductionText={sandbox.intentProductionText}
          onIntentChange={sandbox.handleIntentChange}
          onResetToProduction={sandbox.resetIntentToProduction}
        />
      )}

      {sandbox.promptTab === 2 && (
        <AdminAssistGroundingPromptTab
          groundingText={sandbox.groundingText}
          groundingProductionText={sandbox.groundingProductionText}
          onGroundingChange={sandbox.handleGroundingChange}
          onResetToProduction={sandbox.resetGroundingToProduction}
        />
      )}

      <Divider sx={{ mb: 3 }} />

      <AdminAssistContactSelector
        contactOptions={sandbox.contactOptions}
        contactLoading={sandbox.contactLoading}
        selectedContact={sandbox.selectedContact}
        onSearchChange={sandbox.setContactSearch}
        onContactChange={sandbox.setSelectedContact}
      />

      <AdminAssistTranscriptSection
        selectedContact={sandbox.selectedContact}
        transcript={sandbox.transcript}
        investorComposeText={sandbox.investorComposeText}
        adminComposeText={sandbox.adminComposeText}
        onInvestorComposeChange={sandbox.setInvestorComposeText}
        onAdminComposeChange={sandbox.setAdminComposeText}
        onAddMessage={sandbox.addMessage}
        onResetTranscript={sandbox.resetSandbox}
      />

      <AdminAssistPreviewSection
        sessionId={sandbox.sessionId}
        markdown={sandbox.markdown}
        selectedContact={!!sandbox.selectedContact}
        hasInvestorMessage={sandbox.hasInvestorMessage}
        assistLoading={sandbox.assistLoading}
        assistResult={sandbox.assistResult}
        assistError={sandbox.assistError}
        promptSynced={sandbox.promptSynced}
        intentSynced={sandbox.intentSynced}
        onRunAssist={sandbox.runAssist}
      />

      <Divider sx={{ mb: 3 }} />

      <AdminAssistProposalFooter
        saving={sandbox.saving}
        error={sandbox.error}
        submittedProposal={sandbox.submittedProposal}
        markdown={sandbox.markdown}
        isDirty={sandbox.isDirty}
        intentIsDirty={sandbox.intentIsDirty}
        groundingIsDirty={sandbox.groundingIsDirty}
        promptSynced={sandbox.promptSynced}
        intentSynced={sandbox.intentSynced}
        groundingSynced={sandbox.groundingSynced}
        onSubmitProposal={sandbox.submitProposal}
        onDiscardChanges={sandbox.dismissDraft}
      />
    </>
  );
}
